import React, { useState, useEffect, useRef } from 'react'
import { ArrowLeft, Send, Gift, History, Heart, Calendar, Clock, X, BookOpen, Sparkles, Shirt } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePlayCreateStore } from '../stores/playCreateStore'
import playCreateStorageService from '../services/playCreateStorageService'
import characterAIService from '../services/characterAIService'
import chatToGuidedService from '../services/chatToGuidedService'
import chatSummaryService from '../services/chatSummaryService.js'
import activeMessageService from '../services/activeMessageService.js'
import expressionService from '../services/expressionService.js'
import emotionAnalysisService from '../services/emotionAnalysisService.js'
import { ImageProxyService } from '../services/imageProxyService.js'
import { GiftPanel } from './GiftPanel'
import { HistoryPanel } from './HistoryPanel'
import { DiaryViewer } from './DiaryViewer'
import { ClothingHub } from './ClothingHub'
import toast from 'react-hot-toast'

export const ChatWindow = ({ character, onBack }) => {
  const navigate = useNavigate()
  const { currentMessages, addMessage, setOptions, clearOptions, currentOptions, selectCharacter } = usePlayCreateStore()
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [characterState, setCharacterState] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [showGiftPanel, setShowGiftPanel] = useState(false)
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)
  const [showDiaryViewer, setShowDiaryViewer] = useState(false)
  const [showClothingHub, setShowClothingHub] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const [messages, setMessages] = useState([])
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0) // 当前显示的消息索引
  const [currentSessionId, setCurrentSessionId] = useState(null) // 当前会话ID
  const [noReplyCount, setNoReplyCount] = useState(0) // 未回复次数
  const [currentExpression, setCurrentExpression] = useState('neutral') // 当前表情
  const [expressions, setExpressions] = useState({}) // 表情URL映射
  const [backgroundKey, setBackgroundKey] = useState(Date.now()) // 强制刷新背景图的key
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)
  const activeMessageTimerRef = useRef(null) // 主动消息定时器

  // 加载角色状态和聊天记录
  const [currentCharacter, setCurrentCharacter] = useState(character) // 本地角色状态，确保使用最新数据

  useEffect(() => {
    const loadData = async () => {
      if (!character) return

      // 清除旧的对话选项（切换角色时）
      clearOptions()

      try {
        console.log('🔍 ChatWindow 开始加载角色数据，character.id:', character.id)
        console.log('📥 接收到的 character prop:', {
          id: character.id,
          name: character.name,
          hasAvatar: !!character.avatar,
          hasBackground: !!character.background,
          avatarUrl: character.avatar,
          backgroundUrl: character.background
        })
        
        // 重新从数据库加载最新的角色数据（确保获取最新的avatar和background）
        const latestCharacter = await playCreateStorageService.getCharacterProfile(character.id)
        console.log('💾 从数据库加载的最新角色数据:', {
          found: !!latestCharacter,
          hasAvatar: !!latestCharacter?.avatar,
          hasBackground: !!latestCharacter?.background,
          avatarUrl: latestCharacter?.avatar,
          backgroundUrl: latestCharacter?.background
        })
        
        let characterToUse = latestCharacter || character
        
        // 加载角色状态
        const state = await playCreateStorageService.getCharacterState(character.id)
        setCharacterState(state)
        
        // 如果角色有当前服装ID，加载服装图片
        if (state?.currentClothingId && characterToUse) {
          try {
            const { default: clothingService } = await import('../services/clothingService.js')
            const clothing = await clothingService.getClothingById(state.currentClothingId)
            
            if (clothing) {
              // 优先使用generatedImageUrl（直接URL），避免Blob URL失效
              let imageUrl = null
              if (clothing.generatedImageUrl) {
                imageUrl = clothing.generatedImageUrl
                console.log('✅ 使用generatedImageUrl:', imageUrl.substring(0, 50))
              } else if (clothing.imageBlob) {
                imageUrl = URL.createObjectURL(clothing.imageBlob)
                console.log('✅ 从Blob创建URL:', imageUrl.substring(0, 50))
              } else if (clothing.imageUrl) {
                imageUrl = clothing.imageUrl
                console.log('✅ 使用普通imageUrl:', imageUrl.substring(0, 50))
              }
              
              if (imageUrl) {
                // 更新角色的立绘和背景
                characterToUse = {
                  ...characterToUse,
                  avatar: imageUrl,
                  background: imageUrl
                }
                setBackgroundKey(Date.now()) // 强制刷新背景
              } else {
                console.log('⚠️ 未找到可用的图片URL')
              }
            }
          } catch (error) {
            console.warn('⚠️ 加载服装图片失败:', error)
          }
        }
        
        setCurrentCharacter(characterToUse)
        console.log('✅ 角色数据已加载:', characterToUse?.name, '当前服装ID:', state?.currentClothingId)

        // 加载表情（容错处理）
        try {
          const characterExpressions = await expressionService.getCharacterExpressions(character.id)
          setExpressions(characterExpressions)
        } catch (error) {
          console.warn('⚠️ 加载表情失败（可能数据库未升级）:', error.message)
          // 如果表情加载失败，设置为空对象，不影响其他功能
          setExpressions({})
        }

        // 加载聊天记录
        const session = await playCreateStorageService.getChatSession(character.id)
        if (session && session.messages && session.messages.length > 0) {
          setMessages(session.messages)
          setCurrentMessageIndex(session.messages.length - 1) // 显示最后一条消息
          setCurrentSessionId(session.id || `session-${Date.now()}`)
        } else {
          // 首次聊天
          const sessionId = `session-${Date.now()}`
          setCurrentSessionId(sessionId)
          
          // 如果启用了NSFW模式，让AI生成第一句话（包含NSFW测试）
          if (characterToUse.enableAdultMode) {
            console.log('🔞 [ChatWindow] 检测到NSFW模式，将由AI生成第一句话')
            // 不添加欢迎消息，等待用户发送第一条消息时让AI生成
            setMessages([])
            setCurrentMessageIndex(-1)
          } else {
            // 非NSFW模式，使用预设的欢迎消息
            const welcomeMessage = {
              id: `msg-${Date.now()}`,
              type: 'text',
              content: `你好！我是${currentCharacter?.name || character?.name}，很高兴认识你！`,
              timestamp: Date.now(),
              sender: 'character'
            }
            setMessages([welcomeMessage])
            setCurrentMessageIndex(0)
            await playCreateStorageService.addMessageToSession(character.id, welcomeMessage)
          }
        }
      } catch (error) {
        console.error('❌ 加载数据失败:', error)
      }
    }

    loadData()
  }, [character?.id]) // 依赖character.id而不是整个character对象

  // 当character prop变化时，更新本地状态
  useEffect(() => {
    if (character) {
      setCurrentCharacter(character)
    }
  }, [character])

  // 监听currentCharacter变化，自动刷新背景
  useEffect(() => {
    if (currentCharacter) {
      console.log('🔄 [useEffect] currentCharacter已更新:', {
        id: currentCharacter.id,
        name: currentCharacter.name,
        background: currentCharacter.background
      })
    }
  }, [currentCharacter])

  // 更新当前时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 每分钟更新一次

    return () => clearInterval(timer)
  }, [])

  // 主动消息定时器
  useEffect(() => {
    if (!character || !characterState) return

    // 清除旧的定时器
    if (activeMessageTimerRef.current) {
      clearInterval(activeMessageTimerRef.current)
    }

    // 设置定时器，每30秒检查一次
    activeMessageTimerRef.current = setInterval(async () => {
      try {
        // 获取最后一条消息的时间
        const lastMessage = messages[messages.length - 1]
        if (!lastMessage) return

        const lastMessageTime = lastMessage.timestamp
        const lastMessageIsCharacter = lastMessage.sender === 'character'

        // 如果最后一条消息是角色发送的，检查是否需要主动消息
          if (lastMessageIsCharacter && activeMessageService.shouldSendActiveMessage(
          characterState,
          lastMessageTime,
          noReplyCount
        )) {
          // 生成并发送主动消息
          const activeMessage = await activeMessageService.generateActiveMessage(
            currentCharacter || character,
            messages,
            characterState.favorability,
            noReplyCount
          )

          // 添加到消息列表
          const updatedMessages = [...messages, activeMessage]
          setMessages(updatedMessages)
          setCurrentMessageIndex(updatedMessages.length - 1)
          
          // 保存到会话
          await playCreateStorageService.addMessageToSession(character.id, activeMessage)
          
          // 增加未回复次数
          setNoReplyCount(prev => prev + 1)
        }
      } catch (error) {
        console.error('❌ 主动消息检查失败:', error)
      }
    }, 30000) // 每30秒检查一次

    return () => {
      if (activeMessageTimerRef.current) {
        clearInterval(activeMessageTimerRef.current)
      }
    }
  }, [character, characterState, messages, noReplyCount])

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // 处理返回按钮
  const handleBack = () => {
    clearOptions() // 清除对话选项
    if (onBack) {
      onBack()
    } else {
      // 如果没有提供 onBack 回调，使用浏览器历史记录返回
      navigate(-1)
    }
  }

  // 发送消息
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending || !character) return

    const userMessage = {
      id: `msg-${Date.now()}-user`,
      type: 'text',
      content: inputValue.trim(),
      timestamp: Date.now(),
      sender: 'user'
    }

      setInputValue('')
      setIsSending(true)
      const updatedMessages = [...messages, userMessage]
      setMessages(updatedMessages)
      setCurrentMessageIndex(updatedMessages.length - 1) // 显示用户消息

      try {
        // 保存用户消息
        await playCreateStorageService.addMessageToSession(character.id, userMessage)

        // 生成角色回复
        const characterReply = await characterAIService.generateCharacterReply(
          currentCharacter || character,
          updatedMessages,
          userMessage.content
        )

        const replyMessage = {
          id: `msg-${Date.now()}-char`,
          type: characterReply.type || 'text',
          content: characterReply.content,
          timestamp: characterReply.timestamp || Date.now(),
          sender: 'character',
          imageUrl: characterReply.imageUrl
        }

        const finalMessages = [...updatedMessages, replyMessage]
        setMessages(finalMessages)
        setCurrentMessageIndex(finalMessages.length - 1) // 显示角色回复
        await playCreateStorageService.addMessageToSession(character.id, replyMessage)
        
        // 用户回复后重置未回复计数
        setNoReplyCount(0)

        // 分析情绪并切换表情
        try {
          const emotion = await emotionAnalysisService.analyzeEmotion(replyMessage.content)
          setCurrentExpression(emotion)
        } catch (error) {
          console.warn('情绪分析失败:', error)
        }

      // 计算好感度变化
      const favorabilityDelta = characterAIService.calculateFavorabilityDelta(
        messages,
        userMessage.content,
        characterReply.content
      )
      
      if (favorabilityDelta !== 0) {
        const newFavorability = await playCreateStorageService.updateFavorability(
          character.id,
          favorabilityDelta
        )
        const updatedState = await playCreateStorageService.getCharacterState(character.id)
        setCharacterState(updatedState)
        if (favorabilityDelta > 0) {
          // 显示增强的好感度提示
          toast.success(`好感度 +${favorabilityDelta}`, {
            icon: '💖',
            duration: 3000,
            style: {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)'
            }
          })
        }
      }

      // 更新角色状态（对话次数等）
      await playCreateStorageService.saveCharacterState(character.id, {
        lastChatTime: Date.now()
      })
      const updatedState = await playCreateStorageService.getCharacterState(character.id)
      setCharacterState(updatedState)

      // 生成对话选项
      const options = await characterAIService.generateConversationOptions(
        currentCharacter || character,
        finalMessages,
        characterReply.content
      )
      setOptions(options)

      // 生成角色日记
      try {
        const diary = await characterAIService.generateDiary(
          currentCharacter || character,
          finalMessages,
          updatedState.favorability
        )
        await playCreateStorageService.saveDiary(character.id, {
          ...diary,
          relatedChatIds: [userMessage.id, replyMessage.id]
        })
      } catch (error) {
        console.warn('生成日记失败:', error)
      }

      // 如果消息数量达到阈值（20条），生成会话摘要
      if (finalMessages.length >= 20 && finalMessages.length % 20 === 0) {
        try {
          const summary = await chatSummaryService.generateSummary(
            currentCharacter || character,
            finalMessages,
            currentSessionId || `session-${Date.now()}`
          )
          await playCreateStorageService.saveChatSummary(summary)
          console.log('✅ 会话摘要已生成并保存')
        } catch (error) {
          console.warn('生成会话摘要失败:', error)
        }
      }

    } catch (error) {
      console.error('❌ 发送消息失败:', error)
      toast.error('发送消息失败')
    } finally {
      setIsSending(false)
    }
  }

  // 组件卸载时生成摘要
  useEffect(() => {
    return () => {
      // 组件卸载时生成摘要
      if (messages.length >= 5 && currentSessionId && character) {
        chatSummaryService.generateSummary(character, messages, currentSessionId)
          .then(summary => {
            return playCreateStorageService.saveChatSummary(summary)
          })
          .then(() => {
            console.log('✅ 会话结束，摘要已保存')
          })
          .catch(error => {
            console.warn('保存会话摘要失败:', error)
          })
      }
    }
  }, [messages, currentSessionId, character])

  // 选择对话选项
  const handleSelectOption = async (option) => {
    clearOptions()
    // 直接发送选项内容
    const userMessage = {
      id: `msg-${Date.now()}-user`,
      type: 'text',
      content: option,
      timestamp: Date.now(),
      sender: 'user'
    }

    setIsSending(true)
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setCurrentMessageIndex(updatedMessages.length - 1)

    try {
      // 保存用户消息
      await playCreateStorageService.addMessageToSession(character.id, userMessage)

      // 生成角色回复
      const characterReply = await characterAIService.generateCharacterReply(
        currentCharacter || character,
        updatedMessages,
        option
      )

      const replyMessage = {
        id: `msg-${Date.now()}-char`,
        type: characterReply.type || 'text',
        content: characterReply.content,
        timestamp: characterReply.timestamp || Date.now(),
        sender: 'character',
        imageUrl: characterReply.imageUrl
      }

      const finalMessages = [...updatedMessages, replyMessage]
      setMessages(finalMessages)
      setCurrentMessageIndex(finalMessages.length - 1)
      await playCreateStorageService.addMessageToSession(character.id, replyMessage)
      
      // 用户回复后重置未回复计数
      setNoReplyCount(0)

      // 计算好感度变化
      const favorabilityDelta = characterAIService.calculateFavorabilityDelta(
        updatedMessages,
        option,
        characterReply.content
      )
      
      if (favorabilityDelta !== 0) {
        const newFavorability = await playCreateStorageService.updateFavorability(
          character.id,
          favorabilityDelta
        )
        const updatedState = await playCreateStorageService.getCharacterState(character.id)
        setCharacterState(updatedState)
        if (favorabilityDelta > 0) {
          toast.success(`好感度 +${favorabilityDelta}`)
        }
      }

      // 更新角色状态
      await playCreateStorageService.saveCharacterState(character.id, {
        lastChatTime: Date.now()
      })
      const updatedState = await playCreateStorageService.getCharacterState(character.id)
      setCharacterState(updatedState)

      // 生成对话选项
      const options = await characterAIService.generateConversationOptions(
        currentCharacter || character,
        finalMessages,
        characterReply.content
      )
      setOptions(options)

      // 生成角色日记
      try {
        const diary = await characterAIService.generateDiary(
          currentCharacter || character,
          finalMessages,
          updatedState.favorability
        )
        await playCreateStorageService.saveDiary(character.id, {
          ...diary,
          relatedChatIds: [userMessage.id, replyMessage.id]
        })
      } catch (error) {
        console.warn('生成日记失败:', error)
      }

    } catch (error) {
      console.error('❌ 发送消息失败:', error)
      toast.error('发送消息失败')
    } finally {
      setIsSending(false)
    }
  }

  // 转换为互动内容
  const handleConvertToInteractive = async () => {
    if (messages.length < 5) {
      toast.error('对话记录太少，至少需要5条消息')
      return
    }

    try {
      setIsConverting(true)
      toast.loading('正在转换为互动内容...', { id: 'converting' })

      // 转换为引导模式数据
      const guidedData = await chatToGuidedService.convertToGuidedData(currentCharacter || character, messages)

      // 保存到sessionStorage，供引导模式页面读取
      sessionStorage.setItem('playcreate-converted-data', JSON.stringify({
        ...guidedData,
        source: 'playcreate',
        characterId: character.id,
        convertedAt: Date.now()
      }))

      toast.success('转换成功！正在跳转到编辑页面...', { id: 'converting' })

      // 跳转到故事生成页面，并标记为导入模式
      setTimeout(() => {
        navigate('/?import=playcreate')
      }, 1000)
    } catch (error) {
      console.error('❌ 转换失败:', error)
      toast.error('转换失败：' + error.message, { id: 'converting' })
    } finally {
      setIsConverting(false)
    }
  }

  // 格式化时间
  const formatTime = (date) => {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  }

  // 计算相处天数
  const daysTogether = characterState
    ? playCreateStorageService.calculateDaysTogether(characterState.firstChatTime)
    : 0

  if (!character) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-dark-400">请选择一个角色</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-0 overflow-hidden">
      <div className="relative bg-dark-900 overflow-hidden shadow-2xl" style={{ 
        aspectRatio: '16/9',
        width: '100vw',
        maxWidth: 'min(100vw, calc(100vh * 16 / 9))',
        height: 'auto',
        maxHeight: '100vh'
      }}>
      {/* 背景层 */}
      {(currentCharacter?.background || character?.background) ? (
        <img
          key={`bg-${backgroundKey}`} // 使用key强制重新渲染（不添加时间戳以保持签名URL有效）
          src={currentCharacter?.background || character?.background}
          alt="背景"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          onLoad={() => {
            const bgUrl = currentCharacter?.background || character?.background
            console.log('✅ 背景图片加载成功:', bgUrl)
          }}
          onError={(e) => {
            const bgUrl = currentCharacter?.background || character?.background
            console.error('❌ 背景图片加载失败:', bgUrl)
            // 加载失败时显示渐变背景
            e.target.style.display = 'none'
          }}
        />
      ) : (
        <div 
          className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        />
      )}

      {/* 移除单独的角色立绘层，因为现在场景图已经包含了角色和背景 */}

      {/* UI层 */}
      <div className="relative z-10 h-full flex flex-col">
        {/* 顶部状态栏 - 固定悬浮 */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-dark-900/95 via-dark-900/80 to-transparent backdrop-blur-md px-3 md:px-6 py-3 shadow-lg">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-1.5 bg-dark-800/80 hover:bg-dark-700 text-white rounded-full transition-all hover:scale-105 border border-dark-600/50 text-sm font-medium"
            >
              <ArrowLeft size={18} />
              <span>返回</span>
            </button>

            <div className="flex items-center gap-2 md:gap-6">
              {/* 好感度 - 增强显示 - 响应式 */}
              <div className="flex items-center gap-1 md:gap-2">
                <Heart size={16} className="md:w-5 md:h-5 text-pink-400 animate-pulse" />
                <div className="flex items-center gap-1 md:gap-2">
                  <span className="text-xs md:text-sm text-dark-300 hidden sm:inline">好感度</span>
                  <span className="text-white font-bold text-base md:text-lg">
                    {characterState?.favorability || 0}
                  </span>
                  <span className="text-dark-400 text-xs md:text-sm hidden md:inline">/100</span>
                  <div className="w-20 md:w-32 h-2 md:h-3 bg-dark-700 rounded-full overflow-hidden shadow-inner">
                    <div
                      className={`h-full transition-all duration-500 ease-out ${
                        (characterState?.favorability || 0) < 30
                          ? 'bg-gradient-to-r from-gray-500 to-gray-400'
                          : (characterState?.favorability || 0) < 50
                          ? 'bg-gradient-to-r from-blue-500 to-blue-400'
                          : (characterState?.favorability || 0) < 80
                          ? 'bg-gradient-to-r from-pink-500 to-pink-400'
                          : 'bg-gradient-to-r from-pink-500 via-red-500 to-red-600'
                      }`}
                      style={{ 
                        width: `${characterState?.favorability || 0}%`,
                        boxShadow: `0 0 10px rgba(255, 105, 180, ${(characterState?.favorability || 0) / 100})`
                      }}
                    />
                  </div>
                  {/* 好感度等级显示 - 移动端隐藏 */}
                  <span className={`text-xs font-medium hidden md:inline ${
                    (characterState?.favorability || 0) < 20
                      ? 'text-gray-400'
                      : (characterState?.favorability || 0) < 50
                      ? 'text-blue-400'
                      : (characterState?.favorability || 0) < 80
                      ? 'text-pink-400'
                      : 'text-red-400'
                  }`}>
                    {characterState?.favorability < 20 ? '陌生' :
                     characterState?.favorability < 50 ? '熟悉' :
                     characterState?.favorability < 80 ? '友好' : '亲密'}
                  </span>
                </div>
              </div>

              {/* 相处天数 - 移动端简化 */}
              <div className="flex items-center gap-1 md:gap-2">
                <Calendar size={14} className="md:w-[18px] md:h-[18px] text-blue-400" />
                <span className="text-xs md:text-sm text-white hidden sm:inline">
                  第<span className="font-semibold">{daysTogether}</span>天
                </span>
                <span className="text-xs text-white sm:hidden">{daysTogether}天</span>
              </div>

              {/* 当前时间 - 移动端隐藏 */}
              <div className="hidden md:flex items-center gap-2">
                <Clock size={18} className="text-green-400" />
                <span className="text-sm text-white">{formatTime(currentTime)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 对话区域 - 背景和立绘展示区域 */}
        <div className="flex-1 relative">
          {/* 这个区域用于显示背景和立绘，对话框在下方 */}
        </div>

        {/* 对话框区域 - 固定在底部，不横跨整个屏幕，半透明 - 响应式 */}
        <div className="px-3 md:px-6 py-3 md:py-4">
          <div className="flex justify-center">
            <div className="flex gap-3 items-end">
              {/* 主对话框 */}
              <div className="w-full max-w-xl">
                {/* "正在输入..."提示 - 放在对话框上面 */}
                {isSending && (
                  <div className="mb-2">
                    <div className="bg-dark-700/25 backdrop-blur-sm text-dark-300 rounded-lg px-4 py-2 text-sm border border-dark-600/30">
                      {(currentCharacter?.name || character?.name)}正在输入...
                    </div>
                  </div>
                )}
                
                <div className="bg-dark-800/20 backdrop-blur-md rounded-t-lg border-t border-l border-r border-dark-600/25 px-4 md:px-6 py-3 md:py-4 shadow-2xl">
                  {/* 首次对话提示（NSFW模式） */}
                  {messages.length === 0 && (currentCharacter?.enableAdultMode || character?.enableAdultMode) && (
                    <div className="mb-3 rounded-xl px-5 py-4 bg-primary/10 text-primary-100 border border-primary/30 backdrop-blur-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🔞</span>
                        <span className="text-sm font-medium text-primary">NSFW模式已启用</span>
                      </div>
                      <div className="text-sm text-white/80">
                        开始对话，{(currentCharacter?.name || character?.name)}会以特殊方式和你打招呼~
                      </div>
                    </div>
                  )}
                  
                  {/* 当前消息对话框 - 美化 */}
                  {messages.length > 0 && currentMessageIndex >= 0 && (
                    <div className={`mb-3 rounded-xl px-5 py-4 ${
                      messages[currentMessageIndex].isSystemMessage
                        ? 'bg-amber-500/15 text-amber-100 border border-amber-500/30 shadow-lg'
                        : messages[currentMessageIndex].sender === 'user'
                        ? 'bg-primary/15 text-white border border-primary/30 shadow-lg shadow-primary/10'
                        : 'bg-dark-700/25 text-white border border-dark-600/30 shadow-lg'
                    } backdrop-blur-sm transition-all duration-300`}>
                      <div className="text-xs text-dark-400 mb-1.5 font-medium flex items-center gap-1">
                        {messages[currentMessageIndex].isSystemMessage ? (
                          <>
                            <Gift size={12} className="text-amber-400" />
                            <span className="text-amber-300">系统消息</span>
                          </>
                        ) : (
                          messages[currentMessageIndex].sender === 'user' ? '你' : (currentCharacter?.name || character?.name)
                        )}
                      </div>
                      {messages[currentMessageIndex].type === 'image' && messages[currentMessageIndex].imageUrl ? (
                        <img
                          src={ImageProxyService.shouldUseProxy(messages[currentMessageIndex].imageUrl) 
                            ? ImageProxyService.getProxiedImageUrl(messages[currentMessageIndex].imageUrl)
                            : messages[currentMessageIndex].imageUrl}
                          alt=""
                          className="max-w-full rounded mb-1"
                          onError={async (e) => {
                            const imageUrl = messages[currentMessageIndex].imageUrl
                            // 如果当前已经是代理URL，尝试直接URL
                            if (e.target.src.includes('/proxy/image')) {
                              e.target.src = imageUrl
                              return
                            }
                            // 如果直接URL失败，尝试代理URL
                            if (e.target.src === imageUrl && ImageProxyService.shouldUseProxy(imageUrl)) {
                              e.target.src = ImageProxyService.getProxiedImageUrl(imageUrl)
                            }
                          }}
                        />
                      ) : (
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">
                          {messages[currentMessageIndex].content}
                        </div>
                      )}
                      {messages[currentMessageIndex].sender === 'character' && (
                        <div className="text-xs text-dark-500 mt-1.5">
                          {new Date(messages[currentMessageIndex].timestamp).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 输入区域 */}
                  <div className="flex items-center gap-2">
                    {/* 礼物按钮 */}
                    <button
                      onClick={() => setShowGiftPanel(true)}
                      className="p-2 text-dark-300 hover:text-primary hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors"
                      title="礼物"
                    >
                      <Gift size={20} />
                    </button>

                    {/* 服装中心按钮 */}
                    <button
                      onClick={() => setShowClothingHub(true)}
                      className="p-2 text-dark-300 hover:text-primary hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors"
                      title={`${currentCharacter?.name || character?.name}的衣柜`}
                    >
                      <Shirt size={20} />
                    </button>

                    {/* 历史记录按钮 */}
                    <button
                      onClick={() => setShowHistoryPanel(true)}
                      className="p-2 text-dark-300 hover:text-primary hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors"
                      title="历史记录"
                    >
                      <History size={20} />
                    </button>

                    {/* 日记按钮 */}
                    {characterState && characterState.favorability >= 20 && (
                      <button
                        onClick={() => setShowDiaryViewer(true)}
                        className="p-2 text-dark-300 hover:text-primary hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors"
                        title="角色日记"
                      >
                        <BookOpen size={20} />
                      </button>
                    )}

                    {/* 转换为互动内容按钮 */}
                    {messages.length >= 5 && (
                      <button
                        onClick={handleConvertToInteractive}
                        disabled={isConverting}
                        className="p-2 text-dark-300 hover:text-primary hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="转换为互动内容"
                      >
                        <Sparkles size={20} />
                      </button>
                    )}

                    {/* 输入框 */}
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleSendMessage()
                        }
                      }}
                      placeholder="输入你想说的话..."
                      className="flex-1 bg-dark-700/30 backdrop-blur-sm border border-dark-600/40 text-white rounded-lg px-4 py-2 focus:outline-none focus:bg-dark-700/30 focus:ring-2 focus:ring-primary/50 focus:border-primary/40 text-sm placeholder:text-dark-400"
                      disabled={isSending}
                    />

                    {/* 发送按钮 */}
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isSending}
                      className="p-2 bg-primary/80 hover:bg-primary text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                      title="发送"
                    >
                      <Send size={20} />
                    </button>

                    {/* 结束对话按钮 */}
                    <button
                      onClick={() => {
                        if (window.confirm('确定要结束对话吗？')) {
                          handleBack()
                        }
                      }}
                      className="p-2 text-dark-300 hover:text-red-400 hover:bg-dark-700/40 backdrop-blur-sm rounded-lg transition-colors"
                      title="结束对话"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* 对话选项 - 放在主对话框右侧 */}
              {currentOptions.length > 0 && (
                <div className="flex flex-col gap-2 w-48">
                  <div className="text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded-full border border-primary/30 text-center backdrop-blur-md">
                    💬 回复选项
                  </div>
                  {currentOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectOption(option)}
                      className="group px-3 py-2 bg-dark-800/25 hover:bg-primary/25 text-white rounded-lg border border-primary/25 hover:border-primary/40 transition-all text-left text-xs shadow-md hover:shadow-lg hover:shadow-primary/20 backdrop-blur-md transform hover:scale-[1.02]"
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-primary/60 group-hover:text-primary text-sm">›</span>
                        <span className="line-clamp-2">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 礼物面板 */}
      {showGiftPanel && (
        <GiftPanel
          character={currentCharacter || character}
          onClose={() => setShowGiftPanel(false)}
          onGiftGiven={async (gift) => {
            try {
              // 1. 先添加用户送礼消息
              const giftMessage = {
                id: `msg-${Date.now()}-gift`,
                type: 'text',
                content: `你送给了${currentCharacter?.name || character?.name}一份【${gift.name}】`,
                timestamp: Date.now(),
                sender: 'user',
                isSystemMessage: true // 标记为系统消息
              }
              
              const messagesWithGift = [...messages, giftMessage]
              setMessages(messagesWithGift)
              setCurrentMessageIndex(messagesWithGift.length - 1)
              
              // 保存送礼消息
              await playCreateStorageService.addMessageToSession(character.id, giftMessage)
              
              // 2. 更新角色状态
              const updatedState = await playCreateStorageService.getCharacterState(character.id)
              setCharacterState(updatedState)
              
              // 3. 生成角色对礼物的反应
              setIsSending(true)
              const reaction = await characterAIService.generateGiftReaction(
                currentCharacter || character,
                gift,
                messagesWithGift,
                updatedState.favorability
              )
              
              // 4. 添加角色反应消息
              const reactionMessage = {
                id: `msg-${Date.now()}-gift-reaction`,
                type: 'text',
                content: reaction.content,
                timestamp: reaction.timestamp || Date.now(),
                sender: 'character'
              }
              
              const updatedMessages = [...messagesWithGift, reactionMessage]
              setMessages(updatedMessages)
              setCurrentMessageIndex(updatedMessages.length - 1)
              
              // 保存到聊天记录
              await playCreateStorageService.addMessageToSession(character.id, reactionMessage)
              
              // 5. 更新好感度（根据礼物效果和反应）
              const favorabilityDelta = gift.effect?.favorabilityBonus || 0
              // 如果角色反应是正面的，额外增加好感度
              if (reaction.isPositive) {
                const bonusFavorability = Math.floor(favorabilityDelta * 0.5) // 额外增加50%
                const totalDelta = favorabilityDelta + bonusFavorability
                if (totalDelta > 0) {
                  await playCreateStorageService.updateFavorability(character.id, totalDelta)
                  const newState = await playCreateStorageService.getCharacterState(character.id)
                  setCharacterState(newState)
                  toast.success(`好感度 +${totalDelta}`, { icon: '💖' })
                }
              } else {
                // 如果反应不是特别正面，减少好感度加成
                const reducedDelta = Math.max(0, Math.floor(favorabilityDelta * 0.5))
                if (reducedDelta > 0) {
                  await playCreateStorageService.updateFavorability(character.id, reducedDelta)
                  const newState = await playCreateStorageService.getCharacterState(character.id)
                  setCharacterState(newState)
                  toast.success(`好感度 +${reducedDelta}`, { icon: '💝' })
                }
              }
              
              // 生成对话选项
              const options = await characterAIService.generateConversationOptions(
                character,
                updatedMessages,
                reaction.content
              )
              setOptions(options)
              
            } catch (error) {
              console.error('❌ 处理礼物反应失败:', error)
              toast.error('处理礼物反应失败')
            } finally {
              setIsSending(false)
            }
          }}
        />
      )}

      {/* 历史记录面板 */}
      {showHistoryPanel && (
        <HistoryPanel
          character={character}
          messages={messages}
          onClose={() => setShowHistoryPanel(false)}
        />
      )}

      {/* 服装中心 */}
      {showClothingHub && (
        <ClothingHub
          character={currentCharacter || character}
          onClose={() => setShowClothingHub(false)}
          onClothingGenerated={async () => {
            // 刷新角色数据以显示新服装
            const updatedCharacter = await playCreateStorageService.getCharacterProfile(character.id)
            if (updatedCharacter) {
              setCurrentCharacter(updatedCharacter) // 更新本地角色状态
              toast.success('服装已保存！')
            }
          }}
          onClothingChanged={async (clothing) => {
            // 刷新角色数据以显示新服装
            let updatedCharacter = await playCreateStorageService.getCharacterProfile(character.id)
            const updatedState = await playCreateStorageService.getCharacterState(character.id)
            
            // 如果有当前服装ID，加载服装图片
            if (updatedState?.currentClothingId && updatedCharacter) {
              const { default: clothingService } = await import('../services/clothingService.js')
              const currentClothing = await clothingService.getClothingById(updatedState.currentClothingId)
              
              if (currentClothing) {
                // 优先使用generatedImageUrl（直接URL），避免Blob URL失效
                let imageUrl = null
                if (currentClothing.generatedImageUrl) {
                  imageUrl = currentClothing.generatedImageUrl
                } else if (currentClothing.imageBlob) {
                  imageUrl = URL.createObjectURL(currentClothing.imageBlob)
                } else if (currentClothing.imageUrl) {
                  imageUrl = currentClothing.imageUrl
                }
                
                if (imageUrl) {
                  updatedCharacter = {
                    ...updatedCharacter,
                    avatar: imageUrl,
                    background: imageUrl
                  }
                }
              }
            }
            
            if (updatedCharacter) {
              setCurrentCharacter(updatedCharacter) // 更新本地角色状态
              setBackgroundKey(Date.now()) // 强制刷新背景
            }
          }}
        />
      )}

      {/* 日记查看器 */}
      {showDiaryViewer && (
        <DiaryViewer
          character={currentCharacter || character}
          favorability={characterState?.favorability || 0}
          onClose={() => setShowDiaryViewer(false)}
        />
      )}
      </div>
    </div>
  )
}

